import i18n from '@/i18n';

const ERROR_MESSAGES = {
  email_already_exists: {
    en: 'Email already exists',
    ar: 'البريد الإلكتروني مستخدم بالفعل',
  },
  phone_already_exists: {
    en: 'Phone already exists',
    ar: 'رقم الهاتف مستخدم بالفعل',
  },
  registration_failed: {
    en: 'Registration failed',
    ar: 'فشل إنشاء الحساب',
  },
  invalid_credentials: {
    en: 'Invalid credentials',
    ar: 'بيانات الدخول غير صحيحة',
  },
  account_not_available: {
    en: 'This account is no longer available',
    ar: 'هذا الحساب لم يعد متاحا',
  },
  account_not_verified: {
    en: 'Account not verified. Verification code sent.',
    ar: 'الحساب غير موثق. تم إرسال رمز التحقق.',
  },
  user_not_found: {
    en: 'User not found',
    ar: 'المستخدم غير موجود',
  },
  invalid_or_expired_otp: {
    en: 'The verification code is invalid or expired',
    ar: 'رمز التحقق غير صحيح أو منتهي الصلاحية',
  },
  invalid_otp_code: {
    en: 'Invalid OTP code',
    ar: 'رمز التحقق غير صحيح',
  },
  otp_verification_failed: {
    en: 'OTP verification failed',
    ar: 'فشل التحقق من الرمز',
  },
  failed_to_resend_otp: {
    en: 'Failed to resend OTP',
    ar: 'فشلت إعادة إرسال رمز التحقق',
  },
  failed_to_reset_password: {
    en: 'Failed to reset password',
    ar: 'فشل إعادة تعيين كلمة المرور',
  },
  password_change_failed: {
    en: 'Failed to change password. Check data and try again.',
    ar: 'فشل تغيير كلمة المرور. تحقق من البيانات وحاول مرة أخرى.',
  },
  missing_register_fields: {
    en: 'Missing required fields: name, phone_number, password',
    ar: 'يرجى إدخال الاسم ورقم الهاتف وكلمة المرور',
  },
  missing_login_fields: {
    en: 'Missing required fields: phoneandEmail/email, password',
    ar: 'يرجى إدخال البريد الإلكتروني أو رقم الهاتف وكلمة المرور',
  },
  missing_identifier: {
    en: 'Missing required field: phoneandEmail',
    ar: 'يرجى إدخال البريد الإلكتروني أو رقم الهاتف',
  },
  missing_email: {
    en: 'Missing required field: email',
    ar: 'يرجى إدخال البريد الإلكتروني',
  },
  missing_phone: {
    en: 'Missing required field: phone_number',
    ar: 'يرجى إدخال رقم الهاتف',
  },
  missing_otp: {
    en: 'Missing required field: otp_code',
    ar: 'يرجى إدخال رمز التحقق',
  },
  missing_otp_identifier: {
    en: 'Missing required fields: phoneandEmail, otp_code',
    ar: 'يرجى إدخال البريد الإلكتروني أو رقم الهاتف ورمز التحقق',
  },
  missing_reset_fields: {
    en: 'Missing required fields: phoneandEmail, otp_code, password',
    ar: 'يرجى إدخال البريد الإلكتروني أو رقم الهاتف ورمز التحقق وكلمة المرور',
  },
  non_syrian_email_required: {
    en: 'Email is required for non-Syrian phone numbers',
    ar: 'البريد الإلكتروني مطلوب للأرقام غير السورية',
  },
  new_phone_not_provided: {
    en: 'New phone number not provided',
    ar: 'يرجى إدخال رقم الهاتف الجديد',
  },
  pending_phone_mismatch: {
    en: 'The phone number does not match the pending verification request',
    ar: 'رقم الهاتف لا يطابق طلب التحقق الحالي',
  },
};

const MESSAGE_CODE_MATCHERS = [
  ['email_already_exists', /email.*(already exists|already used)/i],
  ['phone_already_exists', /(phone|phone number|patient phone number).*(already exists|already used)/i],
  ['invalid_credentials', /invalid credentials/i],
  ['account_not_available', /account is no longer available/i],
  ['account_not_verified', /account not verified/i],
  ['user_not_found', /user not found/i],
  ['invalid_or_expired_otp', /verification code is invalid or expired/i],
  ['invalid_otp_code', /invalid otp code/i],
  ['otp_verification_failed', /otp verification failed/i],
  ['failed_to_resend_otp', /failed to resend otp/i],
  ['failed_to_reset_password', /failed to reset password/i],
  ['password_change_failed', /failed to change password/i],
  ['missing_register_fields', /missing required fields: name, phone_number, password/i],
  ['missing_login_fields', /missing required fields: phoneandemail\/email, password/i],
  ['missing_identifier', /missing required field: phoneandemail/i],
  ['missing_email', /missing required field: email/i],
  ['missing_phone', /missing required field: phone_number/i],
  ['missing_otp', /missing required field: otp_code/i],
  ['missing_otp_identifier', /missing required fields: phoneandemail, otp_code$/i],
  ['missing_reset_fields', /missing required fields: phoneandemail, otp_code, password/i],
  ['non_syrian_email_required', /email is required for non-syrian phone numbers/i],
  ['new_phone_not_provided', /new phone number not provided/i],
  ['pending_phone_mismatch', /phone number does not match the pending verification request/i],
  ['registration_failed', /registration failed/i],
];

const getActiveLanguage = () => {
  const language =
    i18n.resolvedLanguage ||
    i18n.language ||
    localStorage.getItem('i18nextLng') ||
    'en';

  return language.toLowerCase().startsWith('ar') ? 'ar' : 'en';
};

const normalizeCode = (value) =>
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_');

const readMessage = (payload) => {
  if (!payload || typeof payload !== 'object') return '';

  const errors = Array.isArray(payload.errors) ? payload.errors : [];
  return (
    payload.message ||
    payload.error ||
    errors.find((item) => typeof item === 'string') ||
    ''
  );
};

const resolveMessageCode = (message) => {
  const text = String(message || '').trim();
  if (!text) return '';

  const match = MESSAGE_CODE_MATCHERS.find(([, pattern]) => pattern.test(text));
  return match?.[0] || '';
};

export const translateApiMessage = (payload, fallback = '') => {
  if (typeof payload === 'string') {
    const code = resolveMessageCode(payload) || resolveMessageCode(fallback);
    const translated = ERROR_MESSAGES[normalizeCode(code)]?.[getActiveLanguage()];
    return translated || payload || fallback;
  }

  const responsePayload =
    payload?.response?.data ||
    (payload?.config || payload?.status || payload?.headers ? payload?.data : payload) ||
    {};
  const rawMessage =
    readMessage(responsePayload) ||
    (payload instanceof Error ? payload.message : '') ||
    fallback;
  const code = normalizeCode(
    responsePayload.error_code ||
      responsePayload.errorCode ||
      responsePayload.code ||
      resolveMessageCode(rawMessage) ||
      resolveMessageCode(fallback)
  );
  const lang = getActiveLanguage();
  const translated = ERROR_MESSAGES[code]?.[lang];

  if (translated) return translated;
  return rawMessage || fallback;
};
